import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Button } from '@/components/ui/button';
import { InputLabel } from '@/components/ui/InputLabel';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import Loader from '@/components/ui/Loader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type { AppError } from '@/features/auth/types';
import type { EvergreenTimelineEvent } from '@/features/webinar/types';
import { getPurchaseNotificationMessage } from '@/features/webinar/evergreenTimelineUtils';
import {
  createEvergreenManualPurchaseNotification,
  deleteEvergreenManualPurchaseNotification,
  deleteEvergreenPurchaseNotification,
  moveEvergreenPurchaseNotification,
  updateEvergreenManualPurchaseNotification,
} from '@/features/webinar/webinarThunks';
import { hmsToSeconds, secondsToHms, type Hms } from '@/lib/evergreenInteractionSchedule';
import {
  areNotificationOffersReady,
  getPurchaseNotifications,
  getReplayedNotificationMessage,
  isManualPurchaseNotification,
  MANUAL_NOTIFICATION_MESSAGE_MAX_LENGTH,
  resolveManualNotificationOfferId,
  toPurchaseNotificationError,
  validateManualPurchaseNotification,
  validatePurchaseNotificationOffset,
  type PurchaseNotificationError,
} from '@/lib/evergreenPurchaseNotifications';
import { toWebinarLng } from '@/lib/webinarLanguage';
import HmsInputs from './HmsInputs';

type PurchaseNotificationsEditorProps = {
  workspaceId: number;
  webinarId: number;
  videoDurationSeconds: number | null;
  readOnly?: boolean;
  isEdit?: boolean;
};

/** Clé du formulaire d'ajout dans les états indexés par notification. */
const NEW_NOTIFICATION_ID = -1;

const EMPTY_HMS: Hms = { h: '', m: '', s: '' };

/** Radix Select refuse une valeur vide : sans offre, la notification suit le thème du webinaire. */
const NO_OFFER_VALUE = 'none';

function withoutKey<T>(record: Record<number, T>, key: number): Record<number, T> {
  const next = { ...record };
  delete next[key];
  return next;
}

export default function PurchaseNotificationsEditor({
  workspaceId,
  webinarId,
  videoDurationSeconds,
  readOnly,
  isEdit,
}: PurchaseNotificationsEditorProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const offerTemplates = useAppSelector((state: RootState) => state.webinar.offerTemplates);
  const pageLoading = useAppSelector(
    (state: RootState) => state.webinar.createWebinarInitialLoading,
  );
  const offersLoading = useAppSelector((state: RootState) => state.webinar.offerTemplatesLoading);
  const offersError = useAppSelector((state: RootState) => state.webinar.offerTemplatesError);
  const offersReady = areNotificationOffersReady({ pageLoading, offersLoading, offersError });
  const webinarLanguage = useAppSelector(
    (state: RootState) => state.webinar.currentWebinar?.language,
  );
  // Le repli doit sortir dans la langue du webinaire, comme la bulle du spectateur.
  const { t: tAttendee, ready: attendeeLabelsReady } = useTranslation('webinars', {
    lng: toWebinarLng(webinarLanguage),
  });
  const fallbackMessage = attendeeLabelsReady
    ? tAttendee('live.chat.purchaseNotificationEvergreen')
    : '';
  const notifications = useMemo(
    () => getPurchaseNotifications(evergreenTimeline),
    [evergreenTimeline],
  );
  const selectableOffers = useMemo(
    () => offerTemplates.filter((offer) => !offer.thankYouPageOffer),
    [offerTemplates],
  );
  const selectableOfferIds = useMemo(
    () => selectableOffers.map((offer) => offer.id),
    [selectableOffers],
  );
  const [isAdding, setIsAdding] = useState(false);
  const [timeDrafts, setTimeDrafts] = useState<Record<number, Hms>>({});
  const [messageDrafts, setMessageDrafts] = useState<Record<number, string>>({});
  const [offerDrafts, setOfferDrafts] = useState<Record<number, number | null>>({});
  const [errors, setErrors] = useState<Record<number, PurchaseNotificationError>>({});
  const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(new Set());
  // Le state n'est relu qu'au rendu suivant : deux clics rapprochés passeraient tous les deux.
  const inFlightRef = useRef(new Set<number>());

  const startPending = (id: number): boolean => {
    if (inFlightRef.current.has(id)) return false;
    inFlightRef.current.add(id);
    setPendingIds(new Set(inFlightRef.current));
    return true;
  };

  const endPending = (id: number) => {
    inFlightRef.current.delete(id);
    setPendingIds(new Set(inFlightRef.current));
  };

  const clearRowState = (id: number) => {
    setTimeDrafts((current) => withoutKey(current, id));
    setMessageDrafts((current) => withoutKey(current, id));
    setOfferDrafts((current) => withoutKey(current, id));
    setErrors((current) => withoutKey(current, id));
  };

  const setRowError = (id: number, error: PurchaseNotificationError) => {
    setErrors((current) => ({ ...current, [id]: error }));
  };

  const getOfferTitle = (event: EvergreenTimelineEvent): string | null => {
    if (event.resourceId == null) return null;
    return offerTemplates.find((o) => o.id === event.resourceId)?.privateTitle ?? null;
  };

  const getTimeValue = (event: EvergreenTimelineEvent): Hms =>
    timeDrafts[event.id] ?? secondsToHms(event.startOffsetSeconds);

  const getMessageValue = (event: EvergreenTimelineEvent): string =>
    messageDrafts[event.id] ?? getPurchaseNotificationMessage(event.payloadJson) ?? '';

  const getOfferValue = (id: number, event: EvergreenTimelineEvent | null): number | null =>
    id in offerDrafts
      ? offerDrafts[id]
      : resolveManualNotificationOfferId(event, selectableOfferIds);

  const updateOfferDraft = (id: number, value: string) => {
    setOfferDrafts((current) => ({
      ...current,
      [id]: value === NO_OFFER_VALUE ? null : Number(value),
    }));
    setErrors((current) => withoutKey(current, id));
  };

  const updateTimeDraft = (id: number, value: Hms) => {
    setTimeDrafts((current) => ({ ...current, [id]: value }));
    setErrors((current) => withoutKey(current, id));
  };

  const updateMessageDraft = (id: number, value: string) => {
    setMessageDrafts((current) => ({ ...current, [id]: value }));
    setErrors((current) => withoutKey(current, id));
  };

  const runPending = async (id: number, action: () => Promise<unknown>) => {
    if (!startPending(id)) return false;
    try {
      await action();
      clearRowState(id);
      return true;
    } catch (error) {
      setRowError(id, toPurchaseNotificationError(error as AppError));
      return false;
    } finally {
      endPending(id);
    }
  };

  const saveManual = async (event: EvergreenTimelineEvent) => {
    if (!offersReady) return;
    const message = getMessageValue(event);
    const time = getTimeValue(event);
    const validationError = validateManualPurchaseNotification(message, time, videoDurationSeconds);
    const offsetSeconds = hmsToSeconds(time);
    if (validationError || offsetSeconds == null) {
      setRowError(event.id, validationError ?? 'INVALID_TIME');
      return;
    }
    await runPending(event.id, () =>
      dispatch(
        updateEvergreenManualPurchaseNotification({
          workspaceId,
          webinarId,
          eventId: event.id,
          input: {
            message: message.trim(),
            startOffsetSeconds: offsetSeconds,
            offerTemplateId: getOfferValue(event.id, event),
          },
        }),
      ).unwrap(),
    );
  };

  const save = async (event: EvergreenTimelineEvent) => {
    if (isManualPurchaseNotification(event)) {
      await saveManual(event);
      return;
    }
    const offsetSeconds = hmsToSeconds(getTimeValue(event));
    const validationError = validatePurchaseNotificationOffset(offsetSeconds, videoDurationSeconds);
    if (validationError || offsetSeconds == null) {
      setRowError(event.id, validationError ?? 'INVALID_TIME');
      return;
    }
    await runPending(event.id, () =>
      dispatch(
        moveEvergreenPurchaseNotification({
          workspaceId,
          webinarId,
          eventId: event.id,
          startOffsetSeconds: offsetSeconds,
        }),
      ).unwrap(),
    );
  };

  const remove = async (event: EvergreenTimelineEvent) => {
    const target = { workspaceId, webinarId, eventId: event.id };
    await runPending(event.id, () =>
      dispatch(
        isManualPurchaseNotification(event)
          ? deleteEvergreenManualPurchaseNotification(target)
          : deleteEvergreenPurchaseNotification(target),
      ).unwrap(),
    );
  };

  const create = async () => {
    if (!offersReady) return;
    const message = messageDrafts[NEW_NOTIFICATION_ID] ?? '';
    const time = timeDrafts[NEW_NOTIFICATION_ID] ?? EMPTY_HMS;
    const validationError = validateManualPurchaseNotification(message, time, videoDurationSeconds);
    const offsetSeconds = hmsToSeconds(time);
    if (validationError || offsetSeconds == null) {
      setRowError(NEW_NOTIFICATION_ID, validationError ?? 'INVALID_TIME');
      return;
    }
    const created = await runPending(NEW_NOTIFICATION_ID, () =>
      dispatch(
        createEvergreenManualPurchaseNotification({
          workspaceId,
          webinarId,
          input: {
            message: message.trim(),
            startOffsetSeconds: offsetSeconds,
            offerTemplateId: getOfferValue(NEW_NOTIFICATION_ID, null),
          },
        }),
      ).unwrap(),
    );
    if (created) setIsAdding(false);
  };

  const cancelCreate = () => {
    clearRowState(NEW_NOTIFICATION_ID);
    setIsAdding(false);
  };

  const renderError = (id: number) => {
    const error = errors[id];
    if (!error) return null;
    return (
      <p className="text-xs text-error">
        {t(`purchaseNotificationsForm.errors.${error}`, {
          max: MANUAL_NOTIFICATION_MESSAGE_MAX_LENGTH,
        })}
      </p>
    );
  };

  const renderMessageInput = (id: number, value: string, disabled: boolean) => (
    <InputLabel
      label={t('purchaseNotificationsForm.messageLabel')}
      className="w-full"
      multiline
      rows={2}
      value={value}
      maxLength={MANUAL_NOTIFICATION_MESSAGE_MAX_LENGTH}
      disabled={disabled}
      onChange={(event) => updateMessageDraft(id, event.target.value)}
    />
  );

  const renderOfferSelect = (id: number, value: number | null, disabled: boolean) => {
    if (!offersReady || selectableOffers.length === 0) return null;
    return (
      <Select
        value={value == null ? NO_OFFER_VALUE : String(value)}
        onValueChange={(next) => updateOfferDraft(id, next)}
        disabled={disabled}
      >
        <SelectTrigger label={t('purchaseNotificationsForm.offerLabel')} className="h-14 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {selectableOffers.map((offer) => (
            <SelectItem key={offer.id} value={String(offer.id)}>
              {offer.privateTitle}
            </SelectItem>
          ))}
          <SelectItem value={NO_OFFER_VALUE}>{t('purchaseNotificationsForm.noOffer')}</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  const creating = pendingIds.has(NEW_NOTIFICATION_ID);

  return (
    <Accordion type="single" collapsible>
      <AccordionItem
        value="item-1"
        className={`bg-white p-0 rounded-[20px] ${isEdit ? 'accordion-page' : ''}`}
      >
        <AccordionTrigger className="rounded-[20px]">
          {t('purchaseNotificationsForm.title')}
        </AccordionTrigger>
        <AccordionContent className="bg-medium p-4 rounded-lg flex flex-col gap-4">
          <p className="text-sm text-secondary">{t('purchaseNotificationsForm.hint')}</p>
          {!offersReady && !readOnly && (
            <p className="text-xs text-secondary">
              {t(
                offersError == null
                  ? 'purchaseNotificationsForm.offersLoading'
                  : 'purchaseNotificationsForm.offersUnavailable',
              )}
            </p>
          )}
          {notifications.map((event, index) => {
            const busy = pendingIds.has(event.id);
            const manual = isManualPurchaseNotification(event);
            const offerTitle = manual ? null : getOfferTitle(event);
            return (
              <div key={event.id} className="flex flex-col gap-4">
                {index > 0 && <Separator className="my-1" />}
                {offerTitle && (
                  <div className="text-black text-sm font-medium truncate">{offerTitle}</div>
                )}
                {manual ? (
                  <>
                    {renderOfferSelect(
                      event.id,
                      getOfferValue(event.id, event),
                      Boolean(readOnly) || busy,
                    )}
                    {renderMessageInput(
                      event.id,
                      getMessageValue(event),
                      Boolean(readOnly) || busy,
                    )}
                  </>
                ) : (
                  <p className="rounded-lg bg-white px-3 py-2 text-sm text-black whitespace-pre-wrap break-words">
                    {getReplayedNotificationMessage(event.payloadJson, fallbackMessage)}
                  </p>
                )}
                <HmsInputs
                  label={t('purchaseNotificationsForm.offsetLabel')}
                  value={getTimeValue(event)}
                  disabled={readOnly || busy}
                  onChange={(value) => updateTimeDraft(event.id, value)}
                />
                {renderError(event.id)}
                {!readOnly && (
                  <div className="flex justify-end gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void remove(event)}
                      aria-label={t('purchaseNotificationsForm.delete')}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      disabled={busy || (manual && !offersReady)}
                      onClick={() => void save(event)}
                    >
                      {busy && <Loader />}
                      {t('purchaseNotificationsForm.save')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {!readOnly && isAdding && (
            <div className="flex flex-col gap-4">
              {notifications.length > 0 && <Separator className="my-1" />}
              {renderOfferSelect(
                NEW_NOTIFICATION_ID,
                getOfferValue(NEW_NOTIFICATION_ID, null),
                creating,
              )}
              {renderMessageInput(
                NEW_NOTIFICATION_ID,
                messageDrafts[NEW_NOTIFICATION_ID] ?? '',
                creating,
              )}
              <HmsInputs
                label={t('purchaseNotificationsForm.offsetLabel')}
                value={timeDrafts[NEW_NOTIFICATION_ID] ?? EMPTY_HMS}
                disabled={creating}
                onChange={(value) => updateTimeDraft(NEW_NOTIFICATION_ID, value)}
              />
              {renderError(NEW_NOTIFICATION_ID)}
              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" disabled={creating} onClick={cancelCreate}>
                  {t('purchaseNotificationsForm.cancel')}
                </Button>
                <Button
                  type="button"
                  disabled={creating || !offersReady}
                  onClick={() => void create()}
                >
                  {creating && <Loader />}
                  {t('purchaseNotificationsForm.save')}
                </Button>
              </div>
            </div>
          )}
          {!readOnly && !isAdding && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              disabled={!offersReady}
              className="flex items-center gap-2 pt-2 disabled:opacity-50"
            >
              <Plus className="size-6" />
              <Label className={offersReady ? 'cursor-pointer' : 'cursor-not-allowed'}>
                {t('purchaseNotificationsForm.add')}
              </Label>
            </button>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
